/**
 * Electron desktop low-latency piano bridge (winmm on Windows).
 * Same NativeSynthSink shape as Android Capacitor plugin.
 */

export type DesktopPianoInfo = {
  ok: boolean;
  backend: "winmm" | "coreaudio" | "none" | string;
  sampleRate: number;
  bufferFrames: number;
  reason?: string;
  platform?: string;
};

type DesktopPianoApi = {
  start: () => Promise<DesktopPianoInfo>;
  getInfo: () => Promise<DesktopPianoInfo>;
  noteOn: (midi: number, velocity?: number) => void;
  noteOff: (midi: number) => void;
  allNotesOff: () => void;
  setMasterVolume: (value: number) => void;
  setMuted: (value: boolean) => void;
};

type DesktopBridge = {
  shell?: string;
  platform?: string;
  desktopPiano?: DesktopPianoApi;
};

function getDesktop(): DesktopBridge | null {
  try {
    return (
      (window as unknown as { pianoTutorDesktop?: DesktopBridge }).pianoTutorDesktop ??
      null
    );
  } catch {
    return null;
  }
}

export function isDesktopPianoAvailable(): boolean {
  const d = getDesktop();
  return Boolean(d?.shell === "electron" && d.desktopPiano?.start);
}

export async function desktopPianoStart(): Promise<DesktopPianoInfo | null> {
  const api = getDesktop()?.desktopPiano;
  if (!api) return null;
  try {
    return await api.start();
  } catch {
    return null;
  }
}

export function desktopPianoNoteOn(midi: number, velocity = 100): void {
  getDesktop()?.desktopPiano?.noteOn(midi, velocity);
}

export function desktopPianoNoteOff(midi: number): void {
  getDesktop()?.desktopPiano?.noteOff(midi);
}

export function desktopPianoAllNotesOff(): void {
  getDesktop()?.desktopPiano?.allNotesOff();
}

export function desktopPianoSetMasterVolume(value: number): void {
  getDesktop()?.desktopPiano?.setMasterVolume(value);
}

export function desktopPianoSetMuted(value: boolean): void {
  getDesktop()?.desktopPiano?.setMuted(value);
}
